import type { ReactElement, ReactNode } from 'react';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { useResource } from '../shared/hooks';
import { useObservable } from '../shared/store';
import { AppShell, type NavGroup } from './AppShell/AppShell';
import { ConnectionBanner } from './ConnectionBanner/ConnectionBanner';
import { TaskIndicatorConnected } from '../contexts/platform/tasks';
import { useConnectionProblem, useDueCount, useNavigate, useRoute, useServices, useSession, useT } from './di';
import { SettingsSection, type RouteKey } from './routes';

/** Собирает навигацию из сессии, прав и справочников блога; счётчик наступивших публикаций приходит по соединению событий (SYS-UC-13). */
export function AppShellConnected({ blogId, children }: { blogId: string | null; children: ReactNode }): ReactElement {
  const { api, session, catalogVersion } = useServices();
  const catalog = useObservable(catalogVersion);
  const { t } = useT();
  const { me } = useSession();
  const route = useRoute();
  const navigate = useNavigate();
  const blog = me?.blogs.find((b) => b.id === blogId) ?? null;
  const can = (permission: BlogPermission): boolean => (blogId ? session.can(blogId, permission) : false);
  const types = useResource(() => (blogId ? api.entities.types(blogId) : Promise.resolve({ success: true, data: [], messages: [] })), [api, blogId, catalog]);
  const dueCount = useDueCount(blogId);
  const connection = useConnectionProblem();

  const isCurrent = (...keys: RouteKey[]): boolean => (route ? keys.includes(route.key) : false);
  const groups: NavGroup[] = [];
  if (blogId) {
    groups.push({
      title: t('nav.content'),
      items: [
        { to: 'posts', params: { blogId }, label: t('nav.posts'), icon: 'posts', current: isCurrent('posts', 'post') },
        { to: 'media', params: { blogId }, label: t('nav.media'), icon: 'image', current: isCurrent('media', 'mediaItem') },
        ...(types.data ?? []).map((type) => ({
          to: 'entities' as const,
          params: { blogId, typeId: type.id },
          label: type.pluralName,
          icon: 'book' as const,
          current: isCurrent('entities', 'entity') && route?.params.typeId === type.id,
        })),
      ],
    });
    groups.push({
      title: t('nav.plan'),
      items: [
        { to: 'queue', params: { blogId }, label: t('nav.queue'), icon: 'queue', current: isCurrent('queue') },
        { to: 'calendar', params: { blogId }, label: t('nav.calendar'), icon: 'calendar', current: isCurrent('calendar') },
        { to: 'due', params: { blogId }, label: t('nav.due'), icon: 'send', current: isCurrent('due', 'publication'), badge: dueCount },
      ],
    });
    if (can(BlogPermission.ManageSettings) || can(BlogPermission.ManageMembers)) {
      groups.push({
        title: t('nav.blog'),
        items: [{ to: 'settings', params: { blogId, section: SettingsSection.Blog }, label: t('nav.settings'), icon: 'settings', current: isCurrent('settings') }],
      });
    }
  }
  // Личное пользователя — отдельно от блога и от администрирования платформы (scenarios.md).
  groups.push({
    title: me?.user.displayName ?? '',
    items: [
      { to: 'blogs', params: {}, label: t('nav.myBlogs'), icon: 'book', current: isCurrent('blogs') },
      { to: 'profile', params: {}, label: t('nav.profile'), icon: 'user', current: isCurrent('profile') },
    ],
  });
  // ADM-RULE-1: группа видна только администратору.
  if (session.isAdmin()) {
    groups.push({
      title: t('nav.adminGroup'),
      admin: true,
      items: [
        { to: 'adminOverview', params: {}, label: t('nav.adminOverview'), icon: 'chart', current: isCurrent('adminOverview') },
        { to: 'adminBlogs', params: {}, label: t('nav.adminBlogs'), icon: 'book', current: isCurrent('adminBlogs') },
        { to: 'adminOnline', params: {}, label: t('nav.adminOnline'), icon: 'pulse', current: isCurrent('adminOnline') },
        { to: 'adminUsers', params: {}, label: t('nav.admin'), icon: 'users', current: isCurrent('adminUsers') },
        { to: 'adminApiKeys', params: {}, label: t('nav.adminApiKeys'), icon: 'key', current: isCurrent('adminApiKeys') },
        { to: 'adminPlans', params: {}, label: t('nav.adminPlans'), icon: 'layers', current: isCurrent('adminPlans') },
        { to: 'adminTasks', params: {}, label: t('nav.adminTasks'), icon: 'clock', current: isCurrent('adminTasks') },
        { to: 'adminBackups', params: {}, label: t('nav.adminBackups'), icon: 'archive', current: isCurrent('adminBackups') },
        { to: 'adminSettings', params: {}, label: t('nav.adminSettings'), icon: 'settings', current: isCurrent('adminSettings') },
      ],
    });
  }

  return (
    <AppShell
      title={blog?.name ?? t('common.appName')}
      groups={groups}
      blogs={me?.blogs ?? []}
      currentBlogId={blogId}
      onSwitchBlog={(id) => navigate('posts', { blogId: id })}
      onLogout={() => void session.logout()}
      pageKey={`${route?.key ?? ''}:${route?.params.typeId ?? ''}`}
      notice={<ConnectionBanner visible={connection.problem} onReconnect={connection.reconnect} />}
      status={(align) => <TaskIndicatorConnected blogId={blogId} align={align} />}
    >
      {children}
    </AppShell>
  );
}
